import { FC, useState, useEffect, useRef } from 'react';
import { useAppDispatch } from '../../store/slice';
import { FtuxSlice } from '../../store/slice/Ftux';
import { ToastMessagesSlice } from '../../store/slice/ToastMessages';

// PACKAGES
import { Modal } from 'react-bootstrap';

// UTILS
import { guid } from '../../libs/utils';
import ImgRem from '../../libs/imgRem';

// TYPES
import apibridge from '../../apibridge';
import { UserSlice } from '../../store/slice/User';

interface OnboardingStep {
	headerImage?: string;
	title: string;
	subtitle: string;
	image: string;
}

const onboardingSteps: OnboardingStep[] = [
	{
		headerImage: '/svg/account-created.svg',
		title: 'Welcome to Little Learners Love Literacy Online',
		subtitle: "Here's a quick tour of the features to help you get started.",
		image: '/img/onboarding-1.png'
	},
	{
		title: 'Access Weekly Plans that guide your lessons and daily activities',
		subtitle:
			'Each weekly plan is organised by day, featuring a series of targeted activities designed to help your students build confidence in reading and spelling.\n\nPlan out your day by delivering a low-variance curriculum to teach LLLL with fidelity, ensuring your students receive high-quality instruction.',
		image: '/img/onboarding-2.png'
	},
	{
		title: 'Teaching Routines',
		subtitle:
			'Use simple, ready-made slide decks to teach high-quality lessons with confidence.\n\nWe’ve made it easy for you to conduct teaching instruction in classrooms and reduce preparation time.\n\nAccess your lessons through the Weekly Plans, or find them all in one convenient place under the Routines tab.\n\nWhen routines are delivered regularly and with fidelity, students know what is expected, can focus on new learning, and are actively engaged in success.',
		image: '/img/onboarding-3.png'
	},
	{
		title: 'The Little Learner’s eLibrary',
		subtitle:
			'With a variety of series available, students can easily access decodable books to practise and apply the skills they have been taught.\n\nThe library is organised by Stages, making it easy to find an appropriate text. The learning focus of each book is clearly identified, helping you assign books with confidence.',
		image: '/img/onboarding-4.png'
	},
	{
		title: 'Manage classes and students',
		subtitle:
			"Add classes and students to their individual accounts and assign books for them to read in the Student eLibrary.\n\nUse Class Management to monitor reading progress and keep student records year-to-year — even when students move up to a new class or year level.",
		image: '/img/onboarding-5.png'
	},
	{
		title: 'Invite teachers from your school to manage students',
		subtitle:
			"Teachers can be invited via email to join your school on LLLL Online. They’ll have access to the same features and tools as you, can be assigned to classes and manage students' progress in learning.",
		image: '/img/onboarding-6.png'
	},
	{
		title: 'Digital Assessments',
		subtitle:
			'Our digitised solution provides you the exact tools you need to conduct and record assessments right here with the press of a button.\n\nRecord <strong>Grapheme-phoneme knowledge</strong>, <strong>Reading (Non-words)</strong> <strong>and Spelling</strong> assessments digitally and analyse results with our comprehensive charts and data tables.\n\nIf you prefer, you can upload or export your assessment data as well!',
		image: '/img/onboarding-7.png'
	},
	{
		title: 'Helpful platform assistance with Resources, Support and the Help Centre',
		subtitle:
			'<strong>• The Resources</strong> area is home to all downloadable print materials, available at any time to support your teaching and classroom activities.\n\n<strong>• The Support</strong> area provides guided articles and videos to help you develop your literacy teaching—useful when you are unsure about how to implement LLLL with fidelity.\n\n<strong>• The Help Centre</strong> provides answers to common questions on how to make the best use of the online platform.',
		image: '/img/onboarding-8.png'
	}
];

interface OnboardingStepProps {
	step: OnboardingStep;
	stepIndex: number;
	totalSteps: number;
	onNext: () => void;
	onBack: () => void;
	animationState: 'idle' | 'fade-out' | 'fade-in';
}

const OnboardingStepComponent: FC<OnboardingStepProps> = ({
	step,
	stepIndex,
	totalSteps,
	onNext,
	onBack,
	animationState
}) => {
	const isFirstStep = stepIndex === 0;
	const isLastStep = stepIndex === totalSteps - 1;

	// Determine animation class for content and image
	const getAnimationClass = () => {
		if (animationState === 'fade-out') return 'fade-out';
		if (animationState === 'fade-in') return 'fade-in';
		return 'fade-in'; // Default to visible
	};

	return (
		<>
			<div
				className="d-flex flex-column border-2 border-end border-hero-blue shadow-sm"
				style={{
					width: 'var(--onboarding-left-width, 620px)',
					backgroundColor: 'var(--b1-pale)',
					minHeight: '100vh',
					padding: '80px'
				}}
			>
				{/* Main Content Body */}
				<div
					className={`d-flex flex-column justify-content-center flex-grow-1 onboarding-step-content ${getAnimationClass()}`}
					style={{ gap: '20px' }}
				>
					{step.headerImage && (
						<div
							className="d-flex justify-content-center align-items-center mx-auto"
							style={{ width: '160px', height: '160px' }}
						>
							<ImgRem src={step.headerImage} alt="Onboarding illustration" width="160" height="160" />
						</div>
					)}
					<h1
						className={`${isFirstStep ? 'h1' : 'h3'} fw-bold text-shades-800 mb-0 ${
							isFirstStep ? 'text-center' : 'text-start'
						}`}
					>
						{step.title}
					</h1>
					<p
						className={`${isFirstStep ? 'h3' : 'fs-5 fw-normal text-dark'} mb-0 text-dark-text ${
							isFirstStep ? 'text-md-center' : 'text-start'
						}`}
						style={{ whiteSpace: 'pre-line' }}
						dangerouslySetInnerHTML={{ __html: step.subtitle }}
					/>
					{isFirstStep && (
						<button type="button" onClick={onNext} className="btn btn-primary w-100 py-3 px-4 rounded-3 fw-bold">
							Let's go!
						</button>
					)}
				</div>

				{!isFirstStep && (
					<div className="d-flex align-items-center justify-content-end" style={{ gap: '12px' }}>
						<button type="button" onClick={onBack} className="btn btn-outline py-3 px-4 rounded-3 fw-bold">
							Back
						</button>

						{/* Pagination Dots */}
						<div className="d-flex align-items-center justify-content-center" style={{ gap: '4px', flex: '1' }}>
							{Array.from({ length: totalSteps }, (_, index) => (
								<div
									key={index}
									className={`onboarding-pagination-dot ${index === stepIndex ? 'active' : 'inactive'}`}
								/>
							))}
						</div>

						<button type="button" onClick={onNext} className="btn btn-primary py-3 px-4 rounded-3 fw-bold">
							{isLastStep ? 'Done' : 'Next'}
						</button>
					</div>
				)}
			</div>

			<div className="flex-fill d-none d-md-block bg-light-blue onboarding-image-container">
				<img
					src={step.image}
					alt={`Onboarding step ${stepIndex + 1}`}
					className={`w-100 h-100 onboarding-step-image ${getAnimationClass()}`}
					style={{ objectFit: 'cover' }}
				/>
			</div>
		</>
	);
};

const OnBoardingFtux: FC = () => {
	const dispatch = useAppDispatch();

	const [showOnboardingModal, setShowOnboardingModal] = useState(true);
	const [currentStep, setCurrentStep] = useState(0);
	const [animationState, setAnimationState] = useState<'idle' | 'fade-out' | 'fade-in'>('idle');
	const transitionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const fadeInTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	const postFtuxCompleted = async () => {
		const response = await apibridge.postFtuxCompleted();
		if (response && response.data) {
			if (response.data.isError) {
				ToastMessagesSlice.actions.add({
					id: guid(),
					type: 'danger',
					heading: 'Onboarding error',
					description: 'Something went wrong while completing the onboarding steps.'
				});
			}
		}
	};

	const handleNext = () => {
		if (currentStep < onboardingSteps.length - 1) {
			// Clear any existing timeouts
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current);
			}
			if (fadeInTimeoutRef.current) {
				clearTimeout(fadeInTimeoutRef.current);
			}

			// Phase 1: Fade out (0.2s)
			setAnimationState('fade-out');

			// Phase 2: After fade-out completes, change step and fade in (0.2s)
			transitionTimeoutRef.current = setTimeout(() => {
				setCurrentStep(currentStep + 1);
				// Use requestAnimationFrame to ensure DOM updates before starting fade-in
				requestAnimationFrame(() => {
					setAnimationState('fade-in');
					// After fade-in completes, return to idle state
					fadeInTimeoutRef.current = setTimeout(() => {
						setAnimationState('idle');
					}, 200);
				});
			}, 200); // Wait for fade-out to complete (0.2s)
		} else {
			// Onboarding complete
			setShowOnboardingModal(false);
			dispatch(FtuxSlice.actions.completeOnboarding());
			dispatch(UserSlice.actions.setUserOnboardingCompleted(true));
			postFtuxCompleted();
		}
	};

	const handleBack = () => {
		if (currentStep > 0) {
			// Clear any existing timeouts
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current);
			}
			if (fadeInTimeoutRef.current) {
				clearTimeout(fadeInTimeoutRef.current);
			}

			// Phase 1: Fade out (0.2s)
			setAnimationState('fade-out');

			// Phase 2: After fade-out completes, change step and fade in (0.2s)
			transitionTimeoutRef.current = setTimeout(() => {
				setCurrentStep(currentStep - 1);
				// Use requestAnimationFrame to ensure DOM updates before starting fade-in
				requestAnimationFrame(() => {
					setAnimationState('fade-in');
					// After fade-in completes, return to idle state
					fadeInTimeoutRef.current = setTimeout(() => {
						setAnimationState('idle');
					}, 200);
				});
			}, 200); // Wait for fade-out to complete (0.2s)
		}
	};

	const handleSkip = () => {
		setShowOnboardingModal(false);
		// User skipped, don't mark as completed
		// They'll see it again on next login
	};

	// Cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current);
			}
			if (fadeInTimeoutRef.current) {
				clearTimeout(fadeInTimeoutRef.current);
			}
		};
	}, []);

	const currentOnboardingStep = onboardingSteps[currentStep];

	return (
		<>
			<Modal show={showOnboardingModal} onHide={handleSkip} className="modal-onboarding modal-fullscreen" fullscreen>
				<Modal.Body className="p-0">
					<div className="d-flex h-100 w-100">
						<OnboardingStepComponent
							step={currentOnboardingStep}
							stepIndex={currentStep}
							totalSteps={onboardingSteps.length}
							onNext={handleNext}
							onBack={handleBack}
							animationState={animationState}
						/>
					</div>
				</Modal.Body>
			</Modal>
		</>
	);
};

export default OnBoardingFtux;
